<script setup lang="ts">
  import type { GlossType } from '@/types/GlossType'
  import Glossary from '@/components/global/Glossary.vue'
  import { computed } from 'vue'
  import VolumeIcon from '@/components/icons/VolumeIcon.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VolumeSlashIcon from '@/components/icons/VolumeSlashIcon.vue'
  import { usePlaySound } from '@/composables/usePlaySound'


  const { element, backgroundColor, useTagColor } = withDefaults(defineProps<{
    element: GlossType
    backgroundColor?: string
    useTagColor?: boolean
  }>(), {
    backgroundColor: 'white',
    useTagColor: false
  })

  const { isPlaying, toggleSound, audioElement } = usePlaySound()

  const color = computed(() => {
    if (useTagColor && CSS.supports('color', element.grammaticalClass.color)) {
      return element.grammaticalClass.color
    } else {
      return backgroundColor
    }
  })
  const audioUri = computed(() => element.audioFile?.metadata?.elementURI)
  const soundIcon = computed(() => isPlaying.value ? VolumeSlashIcon : VolumeIcon)
</script>

<template>
  <div class="gloss" :class="{'audio': !!audioUri}">
    <VgsButton
      v-if="!!audioUri"
      data-testid="audio-button"
      :aria-label="isPlaying ? 'Pause lyd' : 'Spill av lyd'"
      shape="round"
      @click="toggleSound"
    >
      <audio ref="audioElement" :src="element.audioFile?.metadata?.elementURI" />
      <component :is="soundIcon" />
    </VgsButton>
    <Glossary
      v-if="element.translation && element.isClickable"
      :word="element.title"
      :glossary="{ explanation: element.translation }"
    />
    <template v-else>
      {{ element.title }}
    </template>
  </div>
</template>


<style scoped lang="scss">

  .gloss {
    background-color: v-bind(color);
    padding: 18px 26px;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 78px;
    min-width: 100px;

    @include body;

    &.audio {
      padding-left: 10px;
    }
  }
</style>
